import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { logsAPI, franchisesAPI } from '../services/api';
import { toast } from 'react-toastify';
import './LogsPage.css';

const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });

  // Filtres
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [utilisateur, setUtilisateur] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);

  // Options
  const [franchises, setFranchises] = useState([]);

  const actionOptions = [
    { value: 'connexion', label: 'Connexion' },
    { value: 'validation', label: 'Validation' },
    { value: 'rollback', label: 'Rollback' }
  ];

  useEffect(() => {
    loadFranchises();
    loadLogs();
  }, []);

  const loadFranchises = async () => {
    try {
      const response = await franchisesAPI.getAll();
      const options = response.data.franchises.map(f => ({
        value: f.id,
        label: f.nom
      }));
      setFranchises(options);
    } catch (error) {
      console.error('Erreur lors du chargement des franchises:', error);
    }
  };

  const loadLogs = async (page = 1) => {
    try {
      setLoading(true);

      const params = {
        page,
        limit: 50
      };

      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedFranchise) params.franchiseId = selectedFranchise.value;
      if (utilisateur) params.utilisateur = utilisateur;
      if (selectedAction) params.action = selectedAction.value;

      const response = await logsAPI.getAll(params);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);

    } catch (error) {
      console.error('Erreur lors du chargement des logs:', error);
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadLogs(1);
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setSelectedFranchise(null);
    setUtilisateur('');
    setSelectedAction(null);
    setTimeout(() => loadLogs(1), 0);
  };

  const handlePageChange = (newPage) => {
    loadLogs(newPage);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR');
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'connexion':
        return 'badge badge-info';
      case 'validation':
        return 'badge badge-success';
      case 'rollback':
        return 'badge badge-warning';
      default:
        return 'badge';
    }
  };

  return (
    <div className="logs-page">
      <div className="logs-header">
        <h1>Traces et Logs</h1>

        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>Date début:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Date fin:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Franchise:</label>
              <Select
                options={franchises}
                value={selectedFranchise}
                onChange={setSelectedFranchise}
                placeholder="Toutes"
                isClearable
                className="filter-select"
              />
            </div>

            <div className="filter-group">
              <label>Action:</label>
              <Select
                options={actionOptions}
                value={selectedAction}
                onChange={setSelectedAction}
                placeholder="Toutes"
                isClearable
                className="filter-select"
              />
            </div>

            <div className="filter-group">
              <label>Utilisateur:</label>
              <input
                type="text"
                value={utilisateur}
                onChange={(e) => setUtilisateur(e.target.value)}
                placeholder="Nom ou email..."
                className="filter-input"
              />
            </div>
          </div>

          <div className="filters-actions">
            <button className="btn btn-primary" onClick={handleFilter}>
              Filtrer
            </button>
            <button className="btn btn-secondary" onClick={handleReset}>
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      <div className="logs-content">
        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <>
            <div className="logs-table-container">
              <table className="logs-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Action</th>
                    <th>Utilisateur</th>
                    <th>Franchise</th>
                    <th>Langue</th>
                    <th>Carte UUID</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="no-data">
                        Aucun log trouvé
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDate(log.created_at)}</td>
                        <td>
                          <span className={getActionBadgeClass(log.action)}>
                            {log.action}
                          </span>
                        </td>
                        <td>
                          <div className="user-info">
                            <div className="user-name">{log.utilisateur}</div>
                            <div className="user-email">{log.utilisateur_email}</div>
                          </div>
                        </td>
                        <td>{log.franchise_nom || '-'}</td>
                        <td>{log.langue_cible || '-'}</td>
                        <td>
                          <code className="uuid">{log.carte_uuid ? log.carte_uuid.substring(0, 8) + '...' : '-'}</code>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-pagination"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Précédent
                </button>

                <span className="pagination-info">
                  Page {pagination.page} sur {pagination.totalPages} ({pagination.total} résultats)
                </span>

                <button
                  className="btn btn-pagination"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LogsPage;
